import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, User, EnvelopeSimple, Phone, MapPin, Buildings, ArrowRight, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { usePortalLang, setPortalLang } from '../hooks/usePortalLang';
import { PortalLanguageToggle } from '../components/PortalLanguageToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientOnboardPage() {
  const { companyId, sourceTag } = useParams();
  const navigate = useNavigate();
  const { t } = usePortalLang();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '' });

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/onboard/${companyId}`)
      .then(res => setInfo(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Company not found'))
      .finally(() => setLoading(false));
  }, [companyId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error(t('Please fill in all required fields', 'Por favor completa todos los campos requeridos'));
      return;
    }
    setSubmitting(true);
    try {
      // When the signup URL carries a source tag (e.g. /join/:companyId/freddy),
      // forward it so the admin can segment these clients in the dashboard.
      const url = sourceTag
        ? `${API_URL}/api/portal/onboard/${companyId}/${sourceTag}`
        : `${API_URL}/api/portal/onboard/${companyId}`;
      const res = await axios.post(url, form);
      // Auto-login: store portal token & customer so the next click drops them into the dashboard
      if (res.data?.token) {
        localStorage.setItem('portal_token', res.data.token);
        localStorage.setItem('portal_customer', JSON.stringify(res.data.customer || {
          name: res.data.customer_name,
          email: form.email.toLowerCase().trim(),
        }));
        // Persist the language the visitor chose so it follows them across devices
        try {
          const lang = localStorage.getItem('portal_lang') || 'en';
          setPortalLang(lang);
        } catch {}
      }
      setSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Something went wrong', 'Algo salió mal'));
    } finally { setSubmitting(false); }
  };

  const handleGoToPortal = () => {
    navigate('/portal/dashboard');
  };

  const handleSubmitPackage = () => {
    if (success?.tracking_link_id) {
      navigate(`/portal/submit/${success.tracking_link_id}?tour=1`);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#060B15] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#FF9900] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#060B15] flex items-center justify-center px-4">
      <div className="text-center">
        <WarningCircle size={48} className="text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Not Found</h1>
        <p className="text-gray-400 text-sm">{error}</p>
      </div>
    </div>
  );

  const primaryColor = info?.primary_color || '#FF9900';

  return (
    <div className="min-h-screen bg-[#060B15] text-white relative overflow-hidden" data-testid="client-onboard-page">
      {/* Decorative bg elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-[0.03] pointer-events-none" style={{ background: `radial-gradient(circle, ${primaryColor}, transparent)` }} />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-[0.02] pointer-events-none" style={{ background: `radial-gradient(circle, #0080FF, transparent)` }} />

      {/* Header */}
      <div className="border-b border-white/10 bg-white/[0.02] px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          {info?.company_logo ? (
            <img src={`${API_URL}${info.company_logo}`} alt="" className="h-10 rounded" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: primaryColor + '20' }}>
              <Package size={20} style={{ color: primaryColor }} weight="duotone" />
            </div>
          )}
          <div className="flex-1">
            <p className="font-bold text-base">{info?.company_name}</p>
            <p className="text-xs text-gray-500">{t('Package Tracking Portal', 'Portal de Tracking de Paquetes')}</p>
          </div>
          <PortalLanguageToggle variant="dark" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8 relative z-10">
        <AnimatePresence mode="wait">
          {!success ? (
            <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              {/* Welcome message */}
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: primaryColor + '15' }}
                >
                  <Package size={32} style={{ color: primaryColor }} weight="duotone" />
                </motion.div>
                <h1 className="text-2xl font-bold mb-2">{info?.form_heading || t('Create Your Account', 'Crea Tu Cuenta')}</h1>
                <p className="text-gray-400 text-sm max-w-sm mx-auto">{info?.welcome_message}</p>
              </div>

              {/* Form */}
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6" data-testid="onboard-form">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-400 mb-1.5 block font-semibold uppercase tracking-wide">{t('Full Name', 'Nombre Completo')} *</label>
                    <div className="relative">
                      <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                      <Input
                        value={form.name}
                        onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                        placeholder={t('Your full name', 'Tu nombre completo')}
                        className="pl-10 h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-gray-500"
                        required
                        data-testid="onboard-name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-400 mb-1.5 block font-semibold uppercase tracking-wide">{t('Email', 'Correo')} *</label>
                    <div className="relative">
                      <EnvelopeSimple size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                      <Input
                        type="email"
                        value={form.email}
                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                        placeholder="you@email.com"
                        className="pl-10 h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-gray-500"
                        required
                        data-testid="onboard-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-400 mb-1.5 block font-semibold uppercase tracking-wide">{t('Phone', 'Teléfono')} *</label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                      <Input
                        type="tel"
                        value={form.phone}
                        onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                        placeholder="+1 555 123 4567"
                        className="pl-10 h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-gray-500"
                        required
                        data-testid="onboard-phone"
                      />
                    </div>
                  </div>

                  {info?.show_address !== false && (
                    <div>
                      <label className="text-xs text-gray-400 mb-1.5 block font-semibold uppercase tracking-wide">{t('Address', 'Dirección')}</label>
                      <div className="relative">
                        <MapPin size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                        <Input
                          value={form.address}
                          onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                          placeholder={t('Street address', 'Dirección de calle')}
                          className="pl-10 h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-gray-500"
                          data-testid="onboard-address"
                        />
                      </div>
                    </div>
                  )}

                  {info?.show_city !== false && (
                    <div>
                      <label className="text-xs text-gray-400 mb-1.5 block font-semibold uppercase tracking-wide">{t('City', 'Ciudad')}</label>
                      <div className="relative">
                        <Buildings size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                        <Input
                          value={form.city}
                          onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
                          placeholder={t('City', 'Ciudad')}
                          className="pl-10 h-12 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-gray-500"
                          data-testid="onboard-city"
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-xl text-white font-semibold text-base"
                    style={{ background: primaryColor }}
                    data-testid="onboard-submit-btn"
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>{t('Create My Account', 'Crear Mi Cuenta')} <ArrowRight size={18} className="ml-2" /></>
                    )}
                  </Button>
                </form>
              </div>

              {/* How it works mini explainer */}
              <div className="mt-8 grid grid-cols-3 gap-4 text-center">
                {[
                  { num: '1', title: t('Sign Up', 'Regístrate'), desc: t('Create your free account', 'Crea tu cuenta gratis') },
                  { num: '2', title: t('Submit Tracking', 'Envía Tracking'), desc: t('Enter your package tracking numbers', 'Ingresa tus números de tracking') },
                  { num: '3', title: t('Track & Get Updates', 'Rastrea y Recibe Avisos'), desc: t('Follow your packages in real-time', 'Sigue tus paquetes en tiempo real') },
                ].map((step, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center text-sm font-bold" style={{ background: primaryColor + '20', color: primaryColor }}>
                      {step.num}
                    </div>
                    <p className="text-xs font-semibold text-white">{step.title}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{step.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            /* ===== SUCCESS STATE ===== */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
              data-testid="onboard-success"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
              >
                <CheckCircle size={72} weight="duotone" style={{ color: primaryColor }} className="mx-auto mb-4" />
              </motion.div>
              {success.pending_approval ? (
                <>
                  <h2 className="text-2xl font-bold mb-2">
                    {t('Pending Approval', 'Aprobación Pendiente')}
                  </h2>
                  <p className="text-gray-400 text-sm mb-2 max-w-sm mx-auto">
                    {t(
                      "Your signup is in review. You'll get an email once an admin approves your account.",
                      'Tu registro está en revisión. Recibirás un correo cuando un administrador apruebe tu cuenta.'
                    )}
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-2">
                    {success.already_exists ? t('Welcome Back!', '¡Bienvenido de nuevo!') : t('Account Created!', '¡Cuenta Creada!')}
                  </h2>
                  <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
                    {info?.success_message || t("You're all set! You can now submit and track your packages.", '¡Listo! Ya puedes enviar y rastrear tus paquetes.')}
                  </p>

                  <div className="flex flex-col items-center gap-3">
                    <Button
                      onClick={handleGoToPortal}
                      className="h-14 px-8 rounded-xl text-white font-semibold text-lg shadow-lg w-full max-w-sm"
                      style={{ background: primaryColor, boxShadow: `0 8px 30px ${primaryColor}30` }}
                      data-testid="go-to-portal-btn"
                    >
                      {t('Go to My Dashboard', 'Ir a Mi Panel')}
                      <ArrowRight size={22} className="ml-2" />
                    </Button>

                    <Button
                      onClick={handleSubmitPackage}
                      variant="outline"
                      className="h-12 px-6 rounded-xl font-semibold text-sm w-full max-w-sm bg-white/5 border-white/10 text-white hover:bg-white/10"
                      data-testid="submit-package-btn"
                    >
                      {t('Submit a Tracking Number', 'Enviar un Número de Tracking')}
                    </Button>
                  </div>

                  <p className="text-gray-500 text-xs mt-6">
                    {t("You're signed in. Bookmark", 'Ya iniciaste sesión. Guarda')} <span className="text-gray-300">/portal</span> {t('to log back in anytime with your email.', 'para volver a entrar con tu correo cuando quieras.')}
                  </p>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-white/5 bg-[#060B15]/90 backdrop-blur-md px-6 py-3 text-center">
        <p className="text-gray-600 text-xs">Powered by <span className="text-gray-400 font-medium">Track My Package</span></p>
      </div>
    </div>
  );
}
